import { getApiHosts, getDefaultHeaders, spaHeaderOptions, type Env } from '@fff-web/constants';
import { createRequest } from '@fff-web/request';
import type { FetchOptions } from '@fff-web/request';
import type {
  CreateScratcherParams,
  CreateScratcherPrizeParams,
  GetScratcherPrizesParams,
  RedemptionCouponCodeParams,
  UpdateScratcherParams,
  UpdateScratcherPrizeParams,
  WithCookieAuth,
} from '@fff-web/sdk-types';
import type {
  BulkUploadCouponCodes,
  CancelUserSubscriptionError,
  CancelUserSubscriptionResponse,
  ChangeSubscriptionQuantityBody,
  ChangeSubscriptionQuantityError,
  ChangeSubscriptionQuantityResponse,
  CountryLocationApiResponse,
  CreateScratcherEventBody,
  CreateScratcherEventError,
  CreateScratcherEventResponse,
  CreateScratcherPrizeError,
  CreateScratcherPrizeWireBody,
  GetCountryIsoCodeQuery,
  GetScratcherPrizeError,
  GetScratcherPrizeResponse,
  GetShippingFeesResponse,
  GetUserSubscriptionsQuery,
  GetUserSubscriptionsResponse,
  GetUserSubscriptionsV2Error,
  GetUserSubscriptionsV2Response,
  ReactivateUserSubscriptionError,
  ReactivateUserSubscriptionResponse,
  RedemptionCouponCodeData,
  RedemptionCouponCodeQuery,
  RedemptionErrorData,
  ScratcherEventList,
  ScratcherPrize,
  ScratcherPrizeList,
  SubscriptionRenewalList,
  UpdateScratcherEventBody,
  UpdateScratcherEventError,
  UpdateScratcherEventResponse,
  UpdateScratcherPrizeError,
  UpdateScratcherPrizeResponse,
  UpdateScratcherPrizeWireBody,
  UploadCouponCodesError,
  UploadCouponCodesQuery,
  UploadCouponCodesResponse,
  UserScratcherEventList,
} from './types';
import { toCreateScratcherEventBody } from './wire';

/**
 * Growth SDK.
 * @description
 * Growth SDK is a simple function that takes the environment as parameter (`env`) and
 * returns a set of functions to interact with the growth API. The url used
 * will depend on the environment passed when initializing the SDK.
 *
 * The functions from this SDK are meant to be used on any environment, so
 * the redirect and credentials properties are set to the default fetch values.
 * This is because API calls in Maui will always hit the NextJS API routes,
 * which will handle the redirection. This also means that credentials will
 * be sent only on same-origin requests.
 *
 * If you want to use this in the client, you can initialize
 * the SDK with the parameter `spa = true` to set the redirect policy to
 * manual and credentials to be always included. Please remember that this
 * is not recommended as it has potential security implications. In the future
 * this option will be removed.
 *
 * For more information about the Growth API please check the following links:
 * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/
 */
export function growthSdk(env: Env, spa = false, host?: string) {
  if (!env) {
    throw new Error('Environment is required.');
  }

  const options = spa ? spaHeaderOptions : {};
  const defaultHeaders = getDefaultHeaders(env);
  if (!host) {
    const { GROWTH_API_BASE_HOST } = getApiHosts(env, ['GROWTH_API_BASE_HOST']);
    host = `${GROWTH_API_BASE_HOST}`;
  }

  return {
    /**
     * Get country ISO code based on IP address.
     *
     * The response is the full `CountryInfoResponse` (`countryIsoCode`,
     * `countryName`, `errorMsg`, `success`), served as
     * `application/vnd.fabfitfun.v1+json;charset=UTF-8`. The first two are
     * nullable — growth returns null on dev.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/location-controller/retrieveCountry
     */
    getCountryIsoCode: (userIp: string, fetchOptions?: FetchOptions) =>
      createRequest<GetCountryIsoCodeQuery, CountryLocationApiResponse>({
        url: `${host}/location/country-by-ip`,
        fetchOptions: {
          headers: defaultHeaders,
          ...options,
          ...(fetchOptions || {}),
        },
      })({ ipAddress: userIp }),
    /**
     * Redeem a promo code or gift card code.
     *
     * Exactly one of `coupon` / `giftCard` comes back: coupon takes precedence,
     * and the gift card is only looked up when the code is not a coupon. Failure
     * is `ClientErrorResponse` — 400 for expired / already-redeemed / canceled
     * gift card, 404 when the code is neither.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/coupons-controller/getRedemption
     */
    redemptionCouponCode: ({ promoCodeOrGiftCardCode, ...query }: RedemptionCouponCodeParams) =>
      createRequest<RedemptionCouponCodeQuery, RedemptionCouponCodeData, RedemptionErrorData>({
        url: `${host}/coupons/redemption-code/${promoCodeOrGiftCardCode}`,
        fetchOptions: {
          headers: defaultHeaders,
          ...options,
        },
      })(query),
    /**
     * Gets user subscriptions (**v1 — superseded by `getUserSubscriptionsV2`**).
     *
     * Kept only until the last v1 caller migrates; do not add new callers. v1 reports an
     * in-window trial as a seasonal `BOX`, so none of the trial signals exist on it.
     * @param {string} [jwtToken] - The user's JWT for cookie auth.
     * @param {object} [params] - Optional query params.
     * @param {boolean} [params.unCurlEnabled] - When true, includes box subscriptions that are
     *   otherwise feature-flag-gated (the UnCurly migration). Required by callers that read box
     *   subscription state (e.g. the navbar BFF). Always on in v2, which drops the param.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/Subscription%20Service/findSelf_1
     */
    getUserSubscriptions: async (jwtToken?: string, params?: GetUserSubscriptionsQuery) =>
      createRequest<GetUserSubscriptionsQuery, GetUserSubscriptionsResponse>({
        url: `${host}/v1/subscriptions/self`,
        fetchOptions: {
          headers: {
            ...defaultHeaders,
            ...(jwtToken ? { Cookie: `Jwt=${jwtToken}` } : {}),
          },
          ...options,
        },
      })(params),
    /**
     * Gets user subscriptions — **v2, the Unified Trial View**.
     *
     * Prefer this over `getUserSubscriptions`. It takes no query parameters — unCurl is
     * always enabled server-side.
     *
     * For a caller already passing `unCurlEnabled: true`, the **only** change is the trial
     * shape below. The SCHEDULED cadence, the null `nextBillDate`/`expireDate` on seasonal
     * and annual BOX rows, and the disabled customize partial-rollout gate are unCurl
     * behaviours that v1 already returns with the flag set — not v2 deltas.
     *
     * The shape change that matters: an in-window trial is returned as a single
     * `type: 'MEMBERSHIP'` record with `cadence: 'P21D'` and `trialEndAt` set, carrying no
     * customize fields — so a trialing member has **no `BOX` record**, and
     * `type === 'MEMBERSHIP'` alone no longer means Shopping Club. Identify the trial record
     * by `type === 'MEMBERSHIP' && trialEndAt`, and note `trialStartAt` is cleared on a
     * projected trial, so never pair the two.
     *
     * **`trialEndAt` does not mean "currently trialing."** Growth sets the marker on any row
     * whose `productId` is the configured trial box, with no window check, so an out-of-window
     * trial still carries it. Read `active` as well when the question is whether the trial is
     * still running — the status flags survive the projection untouched and are authoritative.
     *
     * The 200 body is the same `SubscriptionDto` v1 returns, so consumer types are unchanged.
     *
     * **Failure: 422**, when an active BOX subscription's customize information cannot be
     * resolved from storefront and growth therefore cannot enrich the response. Not a rare
     * edge — v1 documents the same path as carrying "a material share of live traffic", and
     * v2 shares its enrichment code. The body is the shared-utils `ApiResponse` envelope, so
     * the failure code is at `error.errorType`, not top level; see
     * [GetUserSubscriptionsV2Error]. Treat it as "subscriptions unavailable", never as
     * "this member has none" — the distinction matters for every box/trial branch downstream.
     * @param {string} [jwtToken] - The user's JWT for cookie auth.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/Subscription%20Service/findSelf
     */
    getUserSubscriptionsV2: async (jwtToken?: string) =>
      createRequest<void, GetUserSubscriptionsV2Response, GetUserSubscriptionsV2Error>({
        url: `${host}/v2/subscriptions/self`,
        fetchOptions: {
          headers: {
            ...defaultHeaders,
            ...(jwtToken ? { Cookie: `Jwt=${jwtToken}` } : {}),
          },
          ...options,
        },
      })(),
    /**
     * Gets the authenticated user's shipping fee per subscription cadence.
     *
     * `feeAmountInCents === 0` means that cadence ships free. Growth owns the whole rule —
     * `SWB` and box-only groups are always zero, an all-shipping-exempt group is zero, and
     * otherwise it compares the CatalogVariant sale price total against the free-shipping
     * threshold. A cadence whose variants cannot be resolved is omitted rather than reported
     * with a misleading fee, so treat a missing entry as unknown, not free.
     * @param {string} [jwtToken] - The user's JWT for cookie auth.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/Subscription%20Service/findShippingFees
     */
    getShippingFees: async (jwtToken?: string) =>
      createRequest<void, GetShippingFeesResponse>({
        url: `${host}/v1/subscriptions/self/shipping-fees`,
        fetchOptions: {
          headers: {
            ...defaultHeaders,
            ...(jwtToken ? { Cookie: `Jwt=${jwtToken}` } : {}),
          },
          ...options,
        },
      })(),
    /**
     * Gets the authenticated user's upcoming subscription renewals.
     *
     * Returns a list deduplicated by `(type, customizeSaleType)` — closest date per pair, ordered by
     * `renewalDate` ascending (nulls last). Each entry carries EITHER `renewalDate` OR (`season` +
     * `year`); the server omits unset fields. Empty when no active subscription renews.
     * @param {string} [jwtToken] - The user's JWT for cookie auth.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/Subscription%20Service/getRenewal
     */
    getNextRenewals: async (jwtToken?: string) =>
      createRequest<void, SubscriptionRenewalList>({
        url: `${host}/v1/subscriptions/self/next-renewals`,
        fetchOptions: {
          headers: {
            ...defaultHeaders,
            ...(jwtToken ? { Cookie: `Jwt=${jwtToken}` } : {}),
          },
          ...options,
        },
      })(),
    /**
     * Cancels user subscription (sets it to non-renew).
     *
     * Growth returns the updated subscription item on 200, and the shared-utils
     * `ApiResponse` envelope on 422 (subscription not found). The lookup is scoped to
     * the authenticated caller's own subscriptions, so a uuid belonging to someone else
     * is a 422, not a 403. Unlike `changeQuantity`, this route has no subscription-type
     * restriction, so it is valid for PRODUCT reFills.
     * @param {string} subscriptionId - The subscription uuid.
     * @param {string} [jwtToken] - The user's JWT for cookie auth. Required when calling
     *   from a server context, where no cookie is attached automatically; client callers
     *   running with `spa = true` can omit it and let the browser send the cookie.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/Subscription%20Service/nonRenew
     */
    cancelUserSubscription: async (subscriptionId: string, jwtToken?: string) =>
      createRequest<void, CancelUserSubscriptionResponse, CancelUserSubscriptionError>({
        url: `${host}/v1/subscriptions/self/subscription-uuid/${subscriptionId}/cancel`,
        method: 'PATCH',
        fetchOptions: {
          headers: {
            ...defaultHeaders,
            ...(jwtToken ? { Cookie: `Jwt=${jwtToken}` } : {}),
          },
          ...options,
        },
      })(),
    /**
     * Changes the quantity of a subscription.
     *
     * Contract traps, verified against `growth-api` source — the generated `@Operation`
     * text is misleading on every one of them:
     *
     * - It documents a "membership subscription", but `processQuantityChange` rejects
     *   any `type != PRODUCT`. This route is for PRODUCT reFills only.
     * - It also rejects an inactive or non-renew subscription (400).
     * - Minimum quantity is 1, enforced twice (controller and service), both 400.
     * - An **increase** runs a live stock check and 422s with `"Not enough stock"`. This
     *   is the reachable failure mode; decreases are unchecked.
     * - A no-op change (same quantity) short-circuits and returns 200 without writing.
     * - The documented 400/422 schemas are `SubscriptionItemDto`, which is wrong; the
     *   body is the `ApiResponse` envelope. See `ChangeSubscriptionQuantityError`.
     * @param {string} subscriptionId - The subscription uuid.
     * @param {number} quantity - The new quantity. Must be >= 1.
     * @param {string} [jwtToken] - The user's JWT for cookie auth.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/Subscription%20Service/changeQuantity
     */
    changeSubscriptionQuantity: async (
      subscriptionId: string,
      quantity: number,
      jwtToken?: string,
    ) =>
      createRequest<
        ChangeSubscriptionQuantityBody,
        ChangeSubscriptionQuantityResponse,
        ChangeSubscriptionQuantityError
      >({
        url: `${host}/v1/subscriptions/self/subscription-uuid/${subscriptionId}/quantity`,
        method: 'PATCH',
        fetchOptions: {
          headers: {
            ...defaultHeaders,
            ...(jwtToken ? { Cookie: `Jwt=${jwtToken}` } : {}),
          },
          ...options,
        },
      })({ quantity }),
    /**
     * Reactivates a cancelled subscription.
     *
     * Growth returns the reactivated `SubscriptionItemDto` on 200, and the
     * shared-utils `ApiResponse` envelope on 400 (a conflicting active subscription
     * exists) and 422 (subscription not found).
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/Subscription%20Service/reactivate
     */
    reactivateUserSubscription: async (subscriptionId: string) =>
      createRequest<void, ReactivateUserSubscriptionResponse, ReactivateUserSubscriptionError>({
        url: `${host}/v1/subscriptions/self/subscription-uuid/${subscriptionId}/reactivate`,
        method: 'PATCH',
        fetchOptions: {
          headers: defaultHeaders,
          ...options,
        },
      })(),
    /**
     * Retrieve all scratcher events.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/public-virtual-scratchers-controller/getAllScratcherEvents
     */
    getAllScratcherEvents: async (fetchOptions?: FetchOptions) =>
      createRequest<void, ScratcherEventList>({
        url: `${host}/public/v1/scratchers/events`,
        fetchOptions: {
          headers: defaultHeaders,
          ...options,
          ...(fetchOptions || {}),
        },
      })(),
    /**
     * Retrieve all user scratcher events.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/virtual-scratchers-controller/getAllScratcherEventsSelf
     */
    getAllUserScratcherEvents: async ({ jwtCookie }: WithCookieAuth) =>
      createRequest<void, UserScratcherEventList>({
        url: `${host}/v1/scratchers/events/self`,
        fetchOptions: {
          headers: { ...defaultHeaders, Cookie: jwtCookie },
          ...options,
        },
      })(),
    /**
     * Create new scratcher event.
     *
     * `CreateScratcherBody` stays hand-authored (it carries the curated
     * `eventTypeId: 1`) and `toCreateScratcherEventBody` is the gate between it
     * and the derived wire body.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/virtual-scratchers-controller/createScratcherEvent
     */
    createScratcherEvent: async (
      { jwtCookie, body }: CreateScratcherParams,
      fetchOptions?: FetchOptions,
    ) =>
      createRequest<
        CreateScratcherEventBody,
        CreateScratcherEventResponse,
        CreateScratcherEventError
      >({
        url: `${host}/v1/scratchers/events`,
        method: 'POST',
        fetchOptions: {
          headers: { ...defaultHeaders, Cookie: jwtCookie },
          ...options,
          ...(fetchOptions || {}),
        },
      })(toCreateScratcherEventBody(body)),
    /**
     * Update scratcher event.
     *
     * No mapper: the wire body is field-for-field `Partial<CreateScratcherBody>`,
     * so the `Input` generic carries the derived type directly.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/virtual-scratchers-controller/updateScratcherEvent
     */
    updateScratcherEvent: async (
      { jwtCookie, body, id }: UpdateScratcherParams,
      fetchOptions?: FetchOptions,
    ) =>
      createRequest<
        UpdateScratcherEventBody,
        UpdateScratcherEventResponse,
        UpdateScratcherEventError
      >({
        url: `${host}/v1/scratchers/events/${id}`,
        method: 'PUT',
        fetchOptions: {
          headers: { ...defaultHeaders, Cookie: jwtCookie },
          ...options,
          ...(fetchOptions || {}),
        },
      })(body),
    /**
     * Retrieve a scratcher event's prizes.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/virtual-scratchers-controller/getScratcherPrizesByEventId
     */
    getScratcherPrizes: async (
      { jwtCookie, id }: GetScratcherPrizesParams,
      fetchOptions?: FetchOptions,
    ) =>
      createRequest<void, ScratcherPrizeList>({
        url: `${host}/v1/scratchers/events/${id}/prizes`,
        fetchOptions: {
          headers: { ...defaultHeaders, Cookie: jwtCookie },
          ...options,
          ...(fetchOptions || {}),
        },
      })(),
    /**
     * Retrieve a single scratcher prize.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/virtual-scratchers-controller/getScratcherPrizeById
     */
    getScratcherPrize: async (
      { jwtCookie, id }: GetScratcherPrizesParams,
      fetchOptions?: FetchOptions,
    ) =>
      createRequest<void, GetScratcherPrizeResponse, GetScratcherPrizeError>({
        url: `${host}/v1/scratchers/prizes/${id}`,
        fetchOptions: {
          headers: { ...defaultHeaders, Cookie: jwtCookie },
          ...options,
          ...(fetchOptions || {}),
        },
      })(),
    /**
     * Create new scratcher prize.
     *
     * No mapper: `CreateScratcherPrizeBody` is field-for-field the wire body.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/virtual-scratchers-controller/createScratcherPrize
     */
    createScratcherPrize: async (
      { jwtCookie, body }: CreateScratcherPrizeParams,
      fetchOptions?: FetchOptions,
    ) =>
      createRequest<CreateScratcherPrizeWireBody, ScratcherPrize, CreateScratcherPrizeError>({
        url: `${host}/v1/scratchers/prizes`,
        method: 'POST',
        fetchOptions: {
          headers: { ...defaultHeaders, Cookie: jwtCookie },
          ...options,
          ...(fetchOptions || {}),
        },
      })(body),
    /**
     * Update scratcher prize.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/virtual-scratchers-controller/updateScratcherPrize
     */
    updateScratcherPrize: async (
      { jwtCookie, prizeId, body }: UpdateScratcherPrizeParams,
      fetchOptions?: FetchOptions,
    ) =>
      createRequest<
        UpdateScratcherPrizeWireBody,
        UpdateScratcherPrizeResponse,
        UpdateScratcherPrizeError
      >({
        url: `${host}/v1/scratchers/prizes/${prizeId}`,
        method: 'PUT',
        fetchOptions: {
          headers: { ...defaultHeaders, Cookie: jwtCookie },
          ...options,
          ...(fetchOptions || {}),
        },
      })(body),
    /**
     * Upload coupon codes to bind them to a prize.
     *
     * The query goes on the URL because the `Input` generic carries the multipart
     * body. A 200 (`text/plain`) means the CSV was accepted, not that every coupon
     * was inserted — growth processes it on a background executor.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/virtual-scratchers-controller/uploadCouponCodes
     */
    uploadCouponCodes: async (
      { jwtCookie, file, ...query }: BulkUploadCouponCodes,
      fetchOptions?: FetchOptions,
    ) => {
      const { eventId, prizeId, expirationDate }: UploadCouponCodesQuery = query;
      const queryParams = new URLSearchParams({
        eventId: String(eventId),
        prizeId: String(prizeId),
        expirationDate,
      });

      const formData = new FormData();
      formData.append('file', new Blob([file], { type: 'text/csv' }), 'coupons.csv');

      // Omit Content-Type so fetch sets the multipart boundary automatically
      const { 'Content-Type': _ct, ...multipartHeaders } = defaultHeaders as Record<string, string>;

      return createRequest<FormData, UploadCouponCodesResponse, UploadCouponCodesError>({
        url: `${host}/v1/scratchers/coupons/bulk?${queryParams.toString()}`,
        method: 'POST',
        fetchOptions: {
          headers: { ...multipartHeaders, Cookie: jwtCookie },
          ...options,
          ...(fetchOptions || {}),
        },
      })(formData);
    },
  };
}
